import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { login as apiLogin, resendActivation as apiResendActivation } from "../services/auth";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { usePageTitle } from "../hooks/usePageTitle";
import { isNativeApp } from "../services/capacitor";
import AuthShell, { AuthField, StoreBadges, EuFundingNote } from "../components/auth/AuthShell";
import { GameButton, GameIcon } from "../components/game";

export default function Login() {
  const { setAuth, token } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  /**
   * Where to go after signing in.
   *
   * Only same-site paths are honoured: a `next` that starts with "//" or
   * carries a scheme would let a crafted login link bounce someone to another
   * host straight after they typed their password.
   */
  const nextParam = searchParams.get("next");
  const redirectTo =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/dashboard";
  const { t } = useTranslation();
  // "Ține-mă minte" persists the session and the identifier we last saw, so a
  // returning user finds it filled in even before the browser's own password
  // manager offers the pair. We never store the password ourselves — that is
  // the browser's job, and the autocomplete tokens below let it do it.
  const REMEMBERED_KEY = "igrowth_remembered_login";
  const remembered = (() => {
    try {
      return localStorage.getItem(REMEMBERED_KEY) || "";
    } catch {
      return "";
    }
  })();

  /**
   * Why we are back on this screen, when the app sent us here itself.
   *
   * "idle" = twelve hours without activity. "expired" = the server refused the
   * token. Saying which is the point: the old behaviour was to sit on a signed-in
   * but empty app, and the complaint was never "I was signed out" — it was "I
   * had no idea what had happened".
   */
  const endedReason = searchParams.get("reason");
  const endedMessage =
    endedReason === "idle"
      ? t(
          "auth.session_idle",
          "Sesiunea s-a încheiat după 12 ore fără activitate. Intră din nou în cont."
        )
      : endedReason === "expired"
        ? t("auth.session_expired", "Sesiunea a expirat. Intră din nou în cont.")
        : null;

  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  /**
   * Whether this failure has a button attached to it.
   *
   * A coach added by their club admin has a random password nobody knows, so
   * the only useful thing we can offer is the welcome email again. Telling
   * them that without a way to act on it just moves the dead end.
   */
  const [canResend, setCanResend] = useState(false);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");

  usePageTitle(t("common.login", "Login"));

  // If already authenticated, skip the login screen
  useEffect(() => {
    if (token) {
      nav(redirectTo, { replace: true });
    }
  }, [token, nav]);

  async function handleResend() {
    setResendState("sending");
    try {
      await apiResendActivation(email.trim());
    } catch {
      /* The endpoint answers 200 for everything; a network error is the only
         way to land here, and the message below is still the right advice. */
    }
    setResendState("sent");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setCanResend(false);
    setResendState("idle");
    setLoading(true);
    try {
      const { token, user } = await apiLogin(email, password);
      try {
        if (rememberMe) localStorage.setItem(REMEMBERED_KEY, email.trim());
        else localStorage.removeItem(REMEMBERED_KEY);
      } catch {
        /* private mode — the session still works, we just can't prefill */
      }
      setAuth(user, token, rememberMe);
      nav(redirectTo);
    } catch (e: any) {
      // Show the backend's specific error message instead of a generic
      // "invalid credentials" so users see "verify your email", "demo
      // expired", "account inactive", etc.
      const backendError = e?.response?.data?.error;
      setErr(backendError || t("auth.invalid_credentials"));
      setCanResend(!!e?.response?.data?.can_resend);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      icon={<GameIcon name="trophy" className="w-8 h-8" />}
      title={t("auth.welcome_back", "Bine ai revenit!")}
      subtitle={t("auth.login_subtitle", "Intră în cont și continuă aventura")}
      topRight={<LanguageSwitcher />}
      footer={
        <div className="space-y-4">
          {!isNativeApp() && <StoreBadges />}
          <div className="bg-white/90 rounded-2xl px-4 py-3 flex items-center justify-center gap-5">
            <img src="/assets/cofinantare.png" alt="Cofinanțare Uniunea Europeană" className="h-9 w-auto" />
            <img src="/assets/gov_ro.svg" alt="Guvernul României" className="h-9 w-auto" />
          </div>
          <EuFundingNote light />
        </div>
      }
    >
      {endedMessage && !err && (
        <div className="p-3 mb-4 text-sm border bg-amber-50 border-amber-200 rounded-xl text-amber-800">
          {endedMessage}
        </div>
      )}

      {err && (
        <div className="p-3 mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">
          {err}
          {/* The account needs an email it may never have received. Offer it
              here, rather than leaving "check your inbox" as the only advice
              for somebody whose invitation expired weeks ago. */}
          {canResend && (
            <div className="mt-2">
              {resendState === "sent" ? (
                <p className="text-rose-800">
                  {t(
                    "auth.activation_resent",
                    "Am trimis din nou emailul de activare. Verifică inbox-ul și folderul Spam — linkul e valabil 7 zile."
                  )}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendState === "sending" || !email.trim()}
                  className="font-semibold underline text-rose-800 disabled:opacity-50"
                >
                  {resendState === "sending"
                    ? t("common.loading", "Se trimite...")
                    : t("auth.resend_activation", "Retrimite-mi emailul de activare")}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/*
        Standard autocomplete tokens, on purpose.

        These were previously set to "off" / "new-password" to stop browsers
        autofilling, which also stopped the saved password from ever coming
        back — so "Ține-mă minte" filled in the username and left the password
        box empty, which is the one field worth remembering.

        A browser only ever offers credentials saved for THIS origin, so the
        thing that was being guarded against here does not happen. The real
        bug that motivated it was on the reset/setup pages, where an autofilled
        old password could be saved as the new one; those forms keep
        autoComplete="new-password", which is the correct token for them.
      */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthField
          label={t("auth.email_or_username", "Email sau username")}
          type="text"
          name="username"
          autoComplete="username"
          placeholder={t("auth.email_or_username_ph", "nume@email.com sau username")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <AuthField
          label={t("common.password", "Parolă")}
          type="password"
          name="password"
          autoComplete="current-password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-600">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t("auth.remember_me", "Ține-mă minte")}
        </label>
        <GameButton type="submit" disabled={loading}>
          {loading ? t("common.loading", "Se încarcă...") : t("common.login", "Intră în cont")}
        </GameButton>
      </form>

      <div className="mt-4 text-sm text-center">
        <Link to="/forgot-password" className="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
          {t("auth.forgot_password_link", "Ai uitat parola?")}
        </Link>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm text-center text-slate-500">
        {/* Shown in the app too, now that there is somewhere for it to go.
            Hiding it natively left a parent who installed from the store on a
            login screen with no exit — and the people it was meant to protect
            (athletes waiting on a club invitation) were the ones it stranded. */}
        <div>
          {t("auth.no_account", "Nu ai cont?")}{" "}
          <Link to="/inregistrare" className="font-semibold text-indigo-600 transition-colors hover:text-indigo-700">
            {t("common.register", "Înregistrează-te")}
          </Link>
        </div>
        {/* Club registration stays web-only: it needs a CUI validated against
            ANAF and a long form that has no business on a phone. */}
        {!isNativeApp() && (
          <div>
            {t("auth.club_admin_register", "Ești administrator de club?")}{" "}
            <Link to="/register-club" className="font-semibold text-blue-600 transition-colors hover:text-blue-700">
              {t("auth.register_club_link", "Înregistrează clubul")}
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
